import React, {useEffect, useMemo} from "react";
import {useSelector} from "react-redux";
import {RootState, useAppDispatch} from "../../../../../../redux/store";


// Import slice e types - PATH RELATIVI!
import {
    saveOrdiniLavoro,
    confermaArrivoOrdiniLavoro,
    completaOrdiniLavoro, deleteOrdiniLavoro,
} from "./ordiniLavoroByPrevisioneSlice";

import {ErrorStatusTypes} from "components/common/errorsDeclarations";
import {useForm, SubmitHandler, Controller} from "react-hook-form";

// Custom hooks
import useAutoSave from "components/common/formUtility/useAutoSave";

// Material UI
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import IconButton from "@mui/material/IconButton";
import Autocomplete from "@mui/material/Autocomplete";
import Stack from "@mui/material/Stack";
import CircularProgress from "@mui/material/CircularProgress";
import Tooltip from "@mui/material/Tooltip";
import {NumericFormat} from "react-number-format";
import {DesktopDatePicker} from "@mui/x-date-pickers/DesktopDatePicker";
import {convertToDateTime} from "components/common/dateUtils/convertToDateTime";

// Icons
import SaveIcon from "@mui/icons-material/Save";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import {theme} from "../../../../../theme";
import {OrdineLavoro, STATI_ORDINE_LAVORO} from "../../../../produzione/ordiniLavoro/ordiniLavoroTypes";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import {useAutorizzazioneGruppi} from "../../../../../authentication/useAutorizzazioneGruppi";
import {PermissionTypes} from "../../../../../authentication/permissionsGroups";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import DeleteIcon from "@mui/icons-material/Delete";
import ReplayIcon from '@mui/icons-material/Replay';
import DateToday from "../../../../../common/dateUtils/DateToday";
import {getStatoOdLColor} from "../../../../evopCommon/getStatoColor";

// ============================================
// INTERFACES
// ============================================


interface OrdiniLavoroInlineFormProps {
    ordineLavoro: OrdineLavoro;
    previsioneId: number;
    consentiModifica: boolean;
}

// ============================================
// COMPONENT
// ============================================

const OrdiniLavoroInlineForm = ({
                                    ordineLavoro,
                                    previsioneId,
                                    consentiModifica,
                                }: OrdiniLavoroInlineFormProps) => {
    const dispatch = useAppDispatch();

    // ============================================
    // REACT HOOK FORM SETUP
    // ============================================

    const {
        handleSubmit,
        register,
        formState: {errors, isDirty, isValid, isSubmitting},
        setValue,
        watch,
        control,
        setError,
        getValues,
        clearErrors,
        reset,
    } = useForm<OrdineLavoro>({
        mode: 'onChange',
        defaultValues: ordineLavoro,
    });

    // ============================================
    // SELECTORS
    // ============================================

    const ordineLavoroStatus = useSelector<RootState, ErrorStatusTypes | undefined>(
        (state) => {
            const ordineLavoroInState = state.ordiniLavoroByPrevisioneState.ordiniLavoroByPrevisione[previsioneId]?.results
                                                                                                                  .find(i => i.id === ordineLavoro.id);
            return ordineLavoroInState?.errorsStack?.status;
        }
    );

    const consentiModificaPianificatori = consentiModifica &&
        useAutorizzazioneGruppi([PermissionTypes.PIANIFICATORI, PermissionTypes.COMMERCIALI, PermissionTypes.GESTORI_SISTEMA]) && ['CONF', 'ARR'].includes(ordineLavoro.stato || 'XX')
    const consentiModificaSchedulatori = consentiModifica && useAutorizzazioneGruppi([PermissionTypes.SCHEDULATORI, PermissionTypes.GESTORI_SISTEMA]) && ['DONE', 'ARR'].includes(ordineLavoro.stato || 'XX')


    // ============================================
    // GESTIONE ERRORI SERVER
    // ============================================

    const listaCampi = useMemo(() =>
            Object.keys(ordineLavoro) as Array<keyof OrdineLavoro>,
        [ordineLavoro]
    );

    useEffect(() => {
        clearErrors();
        listaCampi.forEach((field) => {
            if (ordineLavoro?.errorsStack?.fieldsErrors?.[field]) {
                setError(field, {
                    type: "server",
                    message: ordineLavoro.errorsStack.fieldsErrors[field].toString(),
                });
            }
        });
    }, [ordineLavoro?.errorsStack?.fieldsErrors, listaCampi, setError, clearErrors]);

    // ============================================
    // SUBMIT HANDLER
    // ============================================

    const onSubmit: SubmitHandler<OrdineLavoro> = async (data) => {
        if (consentiModifica) {
            const result = await dispatch(saveOrdiniLavoro({
                previsioneId,
                ordineLavoroToSave: data
            }));

            if (result.meta.requestStatus === 'fulfilled') {
                reset(data);
                // onComplete();
            }
        }
    };

    // ============================================
    // AUTOSAVE HOOK
    // ============================================

    const autoSave = useAutoSave({
        watch,
        handleSubmit,
        isDirty,
        isValid,
        delay: 600,
        onSubmit,
        canSubmit: !!ordineLavoro.id && consentiModifica,
    });

    // ============================================
    // AZIONI SPECIALI
    // ============================================

    const handleConfermaArrivo = async () => {
        const qtaArrivata = watch('qta_arrivata') || watch('qta_prevista');
        const dataRicevimento = watch('data_ricevimento') || DateToday();

        const ordineLavoroToSave:OrdineLavoro = {
            ...getValues(),
            qta_arrivata: qtaArrivata,
            stato: "ARR",
            data_ricevimento: dataRicevimento
        }
        if (consentiModifica) {
            const result = await dispatch(saveOrdiniLavoro({
                previsioneId,
                ordineLavoroToSave: ordineLavoroToSave
            }));

            if (result.meta.requestStatus === 'fulfilled') {
                reset(ordineLavoroToSave);
                // onComplete();
            }
        }

    };

    const handleCompleta = async () => {
        const dataConsegnaPrevista = watch('data_consegna_prevista') || DateToday();
        const ordineLavoroToSave:OrdineLavoro = {
            ...getValues(),
            stato: "DONE",
            data_consegna_prevista: dataConsegnaPrevista
        }
        if (consentiModifica) {
            const result = await dispatch(saveOrdiniLavoro({
                previsioneId,
                ordineLavoroToSave: ordineLavoroToSave
            }));

            if (result.meta.requestStatus === 'fulfilled') {
                reset(ordineLavoroToSave);
                // onComplete();
            }
        }
    };

    const handleRollBack = async () => {
        const values = getValues();

        // Conferma con dettaglio dei dati che verranno cancellati
        if (values.stato === 'ARR') {
            const dettagli: string[] = [];
            if (values.data_ricevimento) {
                dettagli.push(`Data arrivo effettivo: ${convertToDateTime(values.data_ricevimento)?.toFormat("dd MMM yyyy") || values.data_ricevimento}`);
            }
            if (values.qta_arrivata) {
                dettagli.push(`Quantità arrivata: ${values.qta_arrivata}`);
            }
            const messaggio = dettagli.length > 0
                ? `Tornare allo stato 'Confermato'?\nVerranno cancellati:\n- ${dettagli.join('\n- ')}`
                : "Tornare allo stato 'Confermato'?";
            if (!window.confirm(messaggio)) return;

            // Pulizia automatica dei campi
            values.data_ricevimento = null;
            values.qta_arrivata = undefined;
        } else if (values.stato === 'DONE') {
            const dettagli: string[] = [];
            if (values.data_consegna_prevista) {
                dettagli.push(`Data consegna prevista: ${convertToDateTime(values.data_consegna_prevista)?.toFormat("dd MMM yyyy") || values.data_consegna_prevista}`);
            }
            const messaggio = dettagli.length > 0
                ? `Tornare allo stato 'Arrivato a terra'?\nVerranno cancellati:\n- ${dettagli.join('\n- ')}`
                : "Tornare allo stato 'Arrivato a terra'?";
            if (!window.confirm(messaggio)) return;

            // Pulizia automatica dei campi
            values.data_consegna_prevista = null;
        }

        if (consentiModifica) {
            const result = await dispatch(saveOrdiniLavoro({
                previsioneId,
                ordineLavoroToSave: values,
                azione: 'RollBack'
            }));

            if (result.meta.requestStatus === 'fulfilled') {
                reset((result.payload as any).data);
            }
        }
    };

    const handleDelete = (ordineLavoro: OrdineLavoro) => {
        if (!previsioneId || !ordineLavoro.id) return;

        // Controllo se può essere cancellato
        if (ordineLavoro.stato === 'DONE') {
            alert("Non è possibile cancellare un ordineLavoro completato");
            return;
        }

        if (window.confirm(`Eliminare l'ordineLavoro del ${convertToDateTime(ordineLavoro.data_consegna_prevista)?.toFormat("dd MMMM yyyy") || "...."}?`)) {
            dispatch(deleteOrdiniLavoro({
                previsioneId: previsioneId,
                ordineLavoroToDelete: ordineLavoro
            }));
        }
    };

    // ============================================
    // RENDER
    // ============================================

    return (
        <form onSubmit={handleSubmit(onSubmit)}>
            <Grid
                container
                spacing={1}
                sx={{
                    py: 1,
                    px: 1,
                    ml: -1,
                    // backgroundColor: theme.palette.action.selected,
                    // borderLeft: `3px solid ${theme.palette.primary.main}`,
                    borderBottom: `1px solid ${theme.palette.divider}`,
                }}
            >
                {/* ID */}
                <Grid size={{xs: 0.4}}>
                    <Typography variant="body2">
                        #{ordineLavoro.id}
                    </Typography>
                </Grid>

                {/* Stato */}
                <Grid size={{xs: 1.2}}>
                    <Chip
                        label={STATI_ORDINE_LAVORO[ordineLavoro.stato as keyof typeof STATI_ORDINE_LAVORO] || ordineLavoro.stato}
                        size="small"
                        color={getStatoOdLColor(ordineLavoro.stato || '')}
                    />
                </Grid>

                {/* Quantità Prevista */}
                <Grid size={{xs: 0.6}}>
                    {consentiModificaPianificatori ?
                        <Controller
                            name="qta_prevista"
                            control={control}
                            render={({field}) => (
                                <NumericFormat
                                    customInput={TextField}
                                    size="small"
                                    disabled={!consentiModificaPianificatori || ordineLavoro.stato != 'CONF'}
                                    variant="standard"
                                    thousandSeparator="."
                                    decimalSeparator=","
                                    decimalScale={1}
                                    value={field.value}
                                    onValueChange={(values) => {
                                        field.onChange(values.floatValue || "");
                                    }}
                                    error={!!errors?.qta_prevista}
                                    helperText={errors?.qta_prevista?.message}
                                />
                            )}
                        />
                        :
                        <Typography variant="body2">
                            {ordineLavoro.qta_prevista || '-'}
                        </Typography>}
                </Grid>

                {/* Data Arrivo Prevista */}
                <Grid size={{xs: 1.2}}>
                    {consentiModificaPianificatori ?
                        <DesktopDatePicker
                            label=""
                            disabled={!consentiModifica || ordineLavoro.stato != 'CONF'}
                            value={convertToDateTime(watch('data_arrivo_prevista'))}
                            onChange={(newValue) => {
                                setValue("data_arrivo_prevista", newValue
                                    ? convertToDateTime(newValue)?.toFormat("yyyy-MM-dd") || ""
                                    : null, {
                                    shouldDirty: true,
                                });
                            }}
                            slotProps={{
                                textField: {
                                    size: "small",
                                    variant: "standard",
                                    error: !!errors?.data_arrivo_prevista,
                                    helperText: errors?.data_arrivo_prevista?.message,
                                },
                                field: {
                                    clearable: true,
                                },
                            }}
                        />
                        :
                        <Typography variant="body2">
                            {convertToDateTime(ordineLavoro.data_arrivo_prevista)?.toFormat("dd MMM yyyy") || '-'}
                        </Typography>
                    }
                </Grid>
                <Grid size={{xs: 1}}>
                    {ordineLavoro.giorni_ritardo && (
                        <Stack direction="row" spacing={0.5} sx={{alignItems: "center"}}>

                            <WarningAmberIcon fontSize="small" color="error"/>
                            <Typography variant="body2" color="error">
                                {ordineLavoro.giorni_ritardo}g
                            </Typography>
                        </Stack>
                    )}
                </Grid>
                {/* Data Ricevimento */}
                <Grid size={{xs: 1.2}}>
                    {consentiModificaPianificatori ?
                        <DesktopDatePicker
                            label=""
                            disabled={!consentiModificaPianificatori}
                            value={convertToDateTime(watch('data_ricevimento'))}
                            onChange={(newValue) => {
                                setValue("data_ricevimento", newValue
                                    ? convertToDateTime(newValue)?.toFormat("yyyy-MM-dd") || ""
                                    : null, {
                                    shouldDirty: true,
                                });
                            }}
                            slotProps={{
                                textField: {
                                    size: "small",
                                    variant: "standard",
                                    error: !!errors?.data_ricevimento,
                                    helperText: errors?.data_ricevimento?.message,
                                },
                                field: {
                                    clearable: true,
                                },
                            }}
                        />
                        :
                        <Typography variant="body2">
                            {convertToDateTime(ordineLavoro.data_ricevimento)?.toFormat("dd MMM yyyy") || '-'}
                        </Typography>
                    }
                </Grid>


                {/* Quantità Arrivata */}
                <Grid size={{xs: 1}}>
                    {consentiModificaPianificatori ?
                        <Controller
                            name="qta_arrivata"
                            control={control}
                            render={({field}) => (
                                <NumericFormat
                                    customInput={TextField}
                                    size="small"
                                    disabled={!consentiModificaPianificatori || watch('stato') === 'DONE'}
                                    variant="standard"
                                    thousandSeparator="."
                                    decimalSeparator=","
                                    decimalScale={1}
                                    value={field.value}
                                    onValueChange={(values) => {
                                        field.onChange(values.floatValue || "");
                                    }}
                                    error={!!errors?.qta_arrivata}
                                    helperText={errors?.qta_arrivata?.message}
                                />
                            )}
                        />
                        :
                        <Typography variant="body2"
                                    fontWeight={ordineLavoro.qta_arrivata ? 'bold' : 'normal'}>
                            {ordineLavoro.qta_arrivata || '-'}
                        </Typography>
                    }
                </Grid>


                {/* Data Consegna Comunicata */}
                <Grid size={{xs: 1.2}}>
                    {consentiModificaSchedulatori ?
                        <DesktopDatePicker
                            label=""
                            disabled={!consentiModificaSchedulatori}
                            value={convertToDateTime(watch('data_consegna_prevista'))}
                            onChange={(newValue) => {
                                setValue("data_consegna_prevista", newValue
                                    ? convertToDateTime(newValue)?.toFormat("yyyy-MM-dd") || ""
                                    : null, {
                                    shouldDirty: true,
                                });
                            }}
                            slotProps={{
                                textField: {
                                    size: "small",
                                    variant: "standard",
                                    error: !!errors?.data_consegna_prevista,
                                    helperText: errors?.data_consegna_prevista?.message,
                                },
                                field: {
                                    clearable: true,
                                },
                            }}
                        />
                        :
                        <Typography variant="body2">
                            {convertToDateTime(ordineLavoro.data_consegna_prevista)?.toFormat("dd MMM yyyy") || '-'}
                        </Typography>
                    }
                </Grid>
                <Grid size={{xs: 3.4}}>
                    <TextField
                        size="small"
                        variant="standard"
                        placeholder="Note..."
                        {...register("note")}
                        disabled={!consentiModifica}
                        error={!!errors?.note}
                        helperText={errors?.note?.message}
                        fullWidth
                        multiline
                        maxRows={2}
                    />
                </Grid>
                {/* Azioni */}
                <Grid size={{xs: 0.8}}>
                    <Stack direction="row" spacing={0.5}>
                        {/* Azioni speciali basate su stato */}
                        {ordineLavoro.stato == 'CONF' && consentiModificaPianificatori && (
                            <Tooltip title="Conferma arrivo">
                                <IconButton
                                    size="small"
                                    color="warning"
                                    onClick={handleConfermaArrivo}
                                    disabled={isSubmitting}
                                >
                                    <LocalShippingIcon fontSize="small"/>
                                </IconButton>
                            </Tooltip>
                        )}

                        {ordineLavoro.stato == 'ARR' && consentiModificaSchedulatori && (
                            <Tooltip title="Completa">
                                <IconButton
                                    size="small"
                                    color="success"
                                    onClick={handleCompleta}
                                    disabled={isSubmitting}
                                >
                                    <DoneAllIcon fontSize="small"/>
                                </IconButton>
                            </Tooltip>
                        )}

                        {/* Salva manuale se dirty */}
                        {isDirty && (
                            <Tooltip title="Salva">
                                <IconButton
                                    size="small"
                                    color="primary"
                                    type="submit"
                                    disabled={!isValid || isSubmitting}
                                >
                                    {isSubmitting || ordineLavoroStatus === ErrorStatusTypes.PENDING ? (
                                        <CircularProgress size={16}/>
                                    ) : (
                                        <SaveIcon fontSize="small"/>
                                    )}
                                </IconButton>
                            </Tooltip>
                        )}

                        {/* RollBack */}
                        {consentiModificaPianificatori && ordineLavoro.stato == 'ARR' && (
                            <IconButton
                                size="small"
                                color="warning"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleRollBack();
                                }}
                            >
                                <ReplayIcon fontSize="small"/>
                            </IconButton>
                        )}

                        {/* RollBack */}
                        {consentiModificaSchedulatori && ordineLavoro.stato == 'DONE' && (
                            <IconButton
                                size="small"
                                color="warning"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleRollBack();
                                }}
                            >
                                <ReplayIcon fontSize="small"/>
                            </IconButton>
                        )}
                        {consentiModificaPianificatori && ordineLavoro.stato == 'CONF' && (
                            <IconButton
                                size="small"
                                color="error"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleDelete(ordineLavoro);
                                }}
                            >
                                <DeleteIcon fontSize="small"/>
                            </IconButton>
                        )}
                        {/* Indicatore successo */}
                        {!isDirty && ordineLavoroStatus === ErrorStatusTypes.SUCCESS && (
                            <CheckCircleIcon color="success" fontSize="small"/>
                        )}
                    </Stack>
                </Grid>

            </Grid>
        </form>
    );
};

export default OrdiniLavoroInlineForm;